import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
  useMemo,
} from 'react';
import { TabContent, TabPane, Tooltip } from 'reactstrap';
import formatCompleteDate from '~/services/formatCompleteDate';
import { useAuth } from '~/context/AuthContext';
import {
  Container,
  SessionsTable,
  TablesAreaHeader,
  HeaderSection,
  TableContainer,
} from './styles';
import TableOperation from './TableOperation';
import TableAvailable from './TableAvailable';
import TableDelivery from './TableDelivery';
import { IconSearch, Input, Switch } from 'takeat-design-system-ui-kit';
import { HeaderButton } from '~/components/NewHeader/styles';
import ArrowClick from '~/assets/img/arrow-red.svg';
import { Pagination } from '~/components/Pagination';
import { useLocalStorage } from '~/hooks/useLocalStorage';
import { FaCar } from 'react-icons/fa6';
import { useHistory } from 'react-router-dom';


function BillsOperation(
  {
    getSessions,
    minimalSessions,
    getMinimalSessions,
    setMinimalSessions,
    inicialDate,
    finalDate,
    getTablesAvailable,
    tablesAvailable,
    updateOpeninBoxOperation,
    orderByTableDate,
    handleSearchInputTables,
    setTablesFilteredSearch,
    setAllTablesAvailable,
    setCount,
    hasFilteredSessions,
    handleUpdate,
    sessionsInCache,
    activeTab,
  },
  ref
) {
  const { websocketManager, user } = useAuth();
  const history = useHistory();

  const sessionsWaitingPayment = minimalSessions.filter(
    (session) => session.status === 'waiting_payment'
  );

  const sessionsOpen = minimalSessions.filter(
    (session) => session.status === 'open' && !session.is_delivery
  );

  const sessionsClosing = minimalSessions.filter(
    (session) => session.status === 'closing'
  );

  const [renderState, setRenderState] = useState();
  const [inputValue, setInputValue] = useState('');

  function setInput() {
    setInputValue('');
  }

  useImperativeHandle(ref, () => {
    return {
      setInput,
    };
  });

  useEffect(() => {
    if (websocketManager) {
      websocketManager.addMessageCallback(2, (data) => {
        if (data.type === 'table-closing') {
          const session = data.item;

          setMinimalSessions((state) => {
            const updatedSessions = [...state];
            const idx = updatedSessions.findIndex((s) => s.id === session.id);

            if (idx !== -1) {
              updatedSessions.splice(idx, 1, session);
            } else {
              updatedSessions.push(session);
            }

            return updatedSessions;
          });
        } else if (
          data.type === 'new-session-payment' ||
          data.type === 'update-session-tax' ||
          data.type === 'update-session-payments'
        ) {
          const delay = new Promise((resolve) => setTimeout(resolve, 500));
          delay.then(() => {
            getMinimalSessions();
          });
        } else if (data.type === 'new-transfer') {
          getTablesAvailable();
        } else if (data.type === 'table-finished') {
          getTablesAvailable();
        }
      });
    }
  }, [
    setMinimalSessions,
    websocketManager,
    getMinimalSessions,
    getTablesAvailable,
  ]);

  const paneRef = useRef(null);

  const allTables = useMemo(() => {
    return [...sessionsWaitingPayment, ...sessionsClosing, ...sessionsOpen, ...tablesAvailable].filter(
      (table) => {
        let numbersToExclude = [-5, -4];

        if (!user?.delivery_info?.is_delivery_allowed) {
          numbersToExclude.push(-3);
        }

        return !numbersToExclude.includes(table.table_number);
      }
    );
  }, [sessionsClosing, sessionsOpen, tablesAvailable, sessionsWaitingPayment]);

  const [tablesPerPage, setTablesPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(allTables.length / tablesPerPage);
  const [usePagination, setUsePagination] = useLocalStorage(
    'usePagination', false
  );
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Memoize the final array to prevent unnecessary re-renders
  const allTablesArray = useMemo(() => {
    
    const currentTableArray = usePagination
      ? allTables.slice(
          (currentPage - 1) * tablesPerPage,
          currentPage * tablesPerPage
        )
      : allTables;
    return user?.has_balcony ? currentTableArray : currentTableArray.filter(table => table.table_type !== 'balcony');
  }, [allTables, usePagination, currentPage, tablesPerPage]);

  useEffect(() => {
    if (paneRef.current) {
      const { height, width } = paneRef.current.getBoundingClientRect();
      const minWidth = 108;
      const columnsAmount = Math.floor(width / minWidth);
      const rowsAmount = Math.floor(height / 108);
      const tablesPerPage = columnsAmount * rowsAmount;

      setTablesPerPage(tablesPerPage);
    }
  }, [allTables.length]);

  return (
    <Container
      style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
      isFirst={activeTab === 0}
    >
      <TablesAreaHeader>
        <HeaderSection>
          <Input
            iconLeft={<IconSearch />}
            placeholder={`Nº ${
              user?.use_tablet_with_qr_code ? 'comanda' : 'mesa'
            }`}
            onChange={(e) => {
              handleSearchInputTables(e.target.value);
              setInputValue(e.target.value);
            }}
            containerStyles={{ width: 150 }}
            value={inputValue}
            name="new-password"
            autoComplete="new-password"
            type="text"
          />

          <HeaderButton
            onClick={() =>
              window.open(
                `https://app.getdemo.com.br/d/894e2f78-3ea7-4444-8e19-aa3dc2ab351d`,
                '_blank'
              )
            }
          >
            <img
              src={ArrowClick}
              alt="icone de clique"
              style={{
                width: 13,
                height: 24,
                color: '#c8131b',
              }}
            />
            <span>Tutorial</span>
          </HeaderButton>
        </HeaderSection>

        <HeaderSection>
          {usePagination && (
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onChangePage={setCurrentPage}
            />
          )}

              <div id="pagination-tooltip">
                <Switch
                  label="Exibir por páginas"
                  checked={usePagination}
                  onChange={(e) => setUsePagination(e.target.checked)}
                  labelPosition="top"
                />
              </div>

              <Tooltip
                placement="bottom"
                target="pagination-tooltip"
                toggle={() => setTooltipOpen((state) => !state)}
                isOpen={tooltipOpen}
                style={{ borderRadius: 8, padding: 8, width: 250 }}
              >
                <p
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    textAlign: 'left',
                  }}
                >Use a exibição por página para otimizar a performance do sistema caso tenha muitas mesas abertas.
                </p>
              </Tooltip>
        </HeaderSection>
      </TablesAreaHeader>

      <TabContent style={{ height: 'calc(100% - 52px)' }} activeTab={activeTab}>
        <div style={{ height: '100%' }} ref={paneRef}>
          <TabPane
            style={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
              gridTemplateRows: 'repeat(auto-fill, 100px)',
              height: '100%',
              overflow: 'auto',
              display: 'grid',
              gap: 8,
            }}
            tabId={0}
          >
            {allTablesArray.map((session) => {
              if (session.status === 'open') {
                return (
                  <TableOperation
                    session={session}
                    sessionId={session.id}
                    key={session.id}
                    getSessions={getSessions}
                    getMinimalSessions={getMinimalSessions}
                    setRenderState={setRenderState}
                    setMinimalSessions={setMinimalSessions}
                    inicialDate={inicialDate}
                    finalDate={finalDate}
                    type="open"
                    getTablesAvailable={getTablesAvailable}
                    updateOpeninBoxOperation={updateOpeninBoxOperation}
                    orderByTableDate={orderByTableDate}
                    minimalSessions={minimalSessions}
                    setTablesFilteredSearch={setTablesFilteredSearch}
                    setAllTablesAvailable={setAllTablesAvailable}
                    setInputValue={setInputValue}
                    handleSearchInputTables={handleSearchInputTables}
                    hasFilteredSessions={hasFilteredSessions}
                  />
                );
              }

              if (session.status === 'waiting_payment') {
                return (
                  <TableOperation
                    session={session}
                    sessionId={session.id}
                    key={session.id}
                    getSessions={getSessions}
                    getMinimalSessions={getMinimalSessions}
                    setRenderState={setRenderState}
                    setMinimalSessions={setMinimalSessions}
                    inicialDate={inicialDate}
                    finalDate={finalDate}
                    type="waiting_payment"
                    getTablesAvailable={getTablesAvailable}
                    updateOpeninBoxOperation={updateOpeninBoxOperation}
                    orderByTableDate={orderByTableDate}
                    minimalSessions={minimalSessions}
                    setTablesFilteredSearch={setTablesFilteredSearch}
                    setAllTablesAvailable={setAllTablesAvailable}
                    setInputValue={setInputValue}
                    handleSearchInputTables={handleSearchInputTables}
                    hasFilteredSessions={hasFilteredSessions}
                  />
                );
              }

              if (session.status === 'closing') {
                return (
                  <TableOperation
                    session={session}
                    sessionId={session.id}
                    key={session.id}
                    getMinimalSessions={getMinimalSessions}
                    setRenderState={setRenderState}
                    setMinimalSessions={setMinimalSessions}
                    inicialDate={inicialDate}
                    finalDate={finalDate}
                    type="closing"
                    getTablesAvailable={getTablesAvailable}
                    minimalSessions={minimalSessions}
                    orderByTableDate={orderByTableDate}
                    updateOpeninBoxOperation={updateOpeninBoxOperation}
                    setInputValue={setInputValue}
                    setTablesFilteredSearch={setTablesFilteredSearch}
                    setAllTablesAvailable={setAllTablesAvailable}
                    handleSearchInputTables={handleSearchInputTables}
                    hasFilteredSessions={hasFilteredSessions}
                  />
                );
              }

              if (session.table_type === 'drive-thru') {
                return (
                  <TableContainer
                    onClick={() => history.push('/drive-thru')}
                    style={{ fontSize: 15 }}
                  >
                    <FaCar fontSize={25} />
                    Drive-Thru
                  </TableContainer>
                );
              }

              if (
                user?.delivery_info?.is_delivery_allowed &&
                session.table_type === 'delivery'
              ) {
                return (
                  <TableDelivery
                    session={session}
                    sessionId={null}
                    key={session.id}
                    getMinimalSessions={getMinimalSessions}
                    setMinimalSessions={setMinimalSessions}
                    inicialDate={inicialDate}
                    finalDate={finalDate}
                    type="available"
                    getTablesAvailable={getTablesAvailable}
                    handleSearchInputTables={handleSearchInputTables}
                    setCount={setCount}
                    setInputValue={setInputValue}
                    setTablesFilteredSearch={setTablesFilteredSearch}
                    setAllTablesAvailable={setAllTablesAvailable}
                  />
                );
              }

              if (session.status === 'available') {
                return (
                  <TableAvailable
                    session={session}
                    sessionId={null}
                    key={session.id}
                    getMinimalSessions={getMinimalSessions}
                    setMinimalSessions={setMinimalSessions}
                    inicialDate={inicialDate}
                    finalDate={finalDate}
                    type={session.table_type}
                    getTablesAvailable={getTablesAvailable}
                    handleSearchInputTables={handleSearchInputTables}
                    setCount={setCount}
                    setInputValue={setInputValue}
                    setTablesFilteredSearch={setTablesFilteredSearch}
                    setAllTablesAvailable={setAllTablesAvailable}
                    handleUpdate={handleUpdate}
                  />
                );
              }
            })}
          </TabPane>
        </div>
        <TabPane tabId={1}>
          <p style={{ marginLeft: 15, marginTop: 15 }}>
            Acompanhe aqui suas comandas caso sua conexão com a internet seja
            interrompida
          </p>
          <p style={{ marginLeft: 15, marginTop: 5 }}>
            <strong>Última atualização:</strong>{' '}
            {sessionsInCache?.updated_at
              ? formatCompleteDate(sessionsInCache.updated_at)
              : '-'}{' '}
          </p>

          <SessionsTable borderless>
            <thead>
              <tr>
                <th>Mesa / Comanda</th>
                <th>Status</th>
                <th>Total</th>
                <th>Pago</th>
                <th>Horário de abertura</th>
              </tr>
            </thead>
            <tbody>
              {sessionsInCache?.sessions
                ?.filter((session) =>
                  session.table_number.toString().startsWith(inputValue)
                )
                .map((session) => (
                  <>
                    <tr>
                      <td>
                        <strong>{session.table_number}</strong>
                      </td>
                      <td>
                        <strong>
                          {session.status === 'open'
                            ? 'Aberta'
                            : 'Em Fechamento'}
                        </strong>
                      </td>
                      <td>
                        <strong>R${session.total_service_price}</strong>
                      </td>
                      <td>
                        <strong>R${session.paid?.toFixed(2)}</strong>
                      </td>
                      <td>
                        <strong>
                          {formatCompleteDate(session.start_time)}
                        </strong>
                      </td>
                    </tr>
                    {session.bills.map((bill) => (
                      <tr>
                        <td />
                        <td>{bill.phone}</td>
                        <td>R${bill.total}</td>
                        <td />
                        <td />
                      </tr>
                    ))}
                  </>
                ))}
            </tbody>
          </SessionsTable>
        </TabPane>
      </TabContent>
    </Container>
  );
}

export default forwardRef(BillsOperation);
